import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { PincodeProvider } from './contexts/PincodeContext';

import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CheckoutPage from './pages/CheckoutPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import ProfilePage from './pages/ProfilePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import ShippingPage from './pages/ShippingPage';
import GSTPage from './pages/GSTPage';
import FAQPage from './pages/FAQPage';
import MaintenancePage from './pages/MaintenancePage';

import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminCategories from './pages/admin/AdminCategories';
import AdminOrders from './pages/admin/AdminOrders';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminBanners from './pages/admin/AdminBanners';
import AdminPincodes from './pages/admin/AdminPincodes';
import AdminReturns from './pages/admin/AdminReturns';
import AdminSettings from './pages/admin/AdminSettings';

import './App.css';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function UserLayout({ children }) {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function AppContent() {
  const { serverError, retryConnection } = useAuth();

  if (serverError) {
    return <MaintenancePage onRetry={retryConnection} />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* User Routes */}
        <Route path="/" element={<UserLayout><HomePage /></UserLayout>} />
      <Route path="/products" element={<UserLayout><ProductsPage /></UserLayout>} />
      <Route path="/product/:id" element={<UserLayout><ProductDetailPage /></UserLayout>} />
      <Route path="/checkout" element={<UserLayout><CheckoutPage /></UserLayout>} />
      <Route path="/orders" element={<UserLayout><OrdersPage /></UserLayout>} />
      <Route path="/order/:id" element={<UserLayout><OrderDetailPage /></UserLayout>} />
      <Route path="/profile" element={<UserLayout><ProfilePage /></UserLayout>} />
      <Route path="/about" element={<UserLayout><AboutPage /></UserLayout>} />
      <Route path="/contact" element={<UserLayout><ContactPage /></UserLayout>} />
      <Route path="/privacy" element={<UserLayout><PrivacyPolicyPage /></UserLayout>} />
      <Route path="/terms" element={<UserLayout><TermsPage /></UserLayout>} />
      <Route path="/shipping" element={<UserLayout><ShippingPage /></UserLayout>} />
      <Route path="/gst" element={<UserLayout><GSTPage /></UserLayout>} />
      <Route path="/faq" element={<UserLayout><FAQPage /></UserLayout>} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="categories" element={<AdminCategories />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="returns" element={<AdminReturns />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="banners" element={<AdminBanners />} />
        <Route path="pincodes" element={<AdminPincodes />} />
      </Route>
    </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PincodeProvider>
          <CartProvider>
            <AppContent />
            <Toaster position="top-center" />
          </CartProvider>
        </PincodeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
